
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase, isSupabaseConfigured } from '../services/supabaseClient';
import { UserData } from '../types';

interface LoginViewProps {
  onLogin: (userData: UserData) => void;
}

const LoginView: React.FC<LoginViewProps> = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    if (!isSupabaseConfigured) {
      setTimeout(() => {
        onLogin({ email, name: 'Avocat Demo', status: 'active', isApproved: true });
        navigate('/dashboard');
        setLoading(false);
      }, 1000);
      return;
    }

    try {
      const { data, error: authError } = await supabase.auth.signInWithPassword({
        email: email.toLowerCase().trim(),
        password
      });

      if (authError) throw authError;

      if (data.user) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', data.user.id)
          .single();

        onLogin({
          email: data.user.email || '',
          name: profile?.name || data.user.user_metadata?.name || 'Avocat',
          bar: profile?.bar || data.user.user_metadata?.bar,
          barCardNumber: profile?.bar_card_number || data.user.user_metadata?.barCardNumber,
          isApproved: profile?.is_approved ?? data.user.user_metadata?.isApproved ?? false,
          status: 'active'
        });
        
        navigate('/dashboard', { replace: true });
      }
    } catch (err: any) {
      console.error("Login error details:", err);
      setError("Email sau parolă incorectă.");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!email) {
      setError("Te rugăm să introduci adresa de email mai sus.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/#/auth/callback`,
      });
      if (error) throw error;
      setError("✅ Link-ul de resetare a fost trimis pe email.");
    } catch (err: any) {
      console.error("Reset password error details:", err);
      if (err.message?.includes('rate limit')) {
        setError("Te rugăm să aștepți 60 de secunde înainte de a cere un link nou.");
      } else {
        setError(`Eroare la trimiterea email-ului de resetare. Detalii în consolă.`);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#020617] flex items-center justify-center p-6 relative overflow-hidden">
      {/* Background Aurora Effects */}
      <div className="absolute inset-0 z-0">
        <div className="absolute top-[-20%] left-[-10%] w-[70%] h-[70%] bg-indigo-600/20 rounded-full blur-[140px] animate-pulse"></div>
        <div className="absolute bottom-[-20%] right-[-10%] w-[70%] h-[70%] bg-blue-600/15 rounded-full blur-[140px] animate-pulse" style={{animationDelay: '3s'}}></div>
      </div>

      <div className="w-full max-w-md relative z-10 animate-in fade-in slide-in-from-bottom-8 duration-1000">
        <div className="text-center mb-10">
          <div className="w-20 h-20 bg-gradient-to-br from-indigo-500 to-indigo-700 rounded-[2rem] flex items-center justify-center shadow-2xl mx-auto mb-6 border border-white/20 transform hover:rotate-6 transition-transform">
            <span className="text-white font-black text-4xl italic">L</span>
          </div>
          <h2 className="text-4xl font-black text-white mb-2 italic tracking-tighter">LexSmart AI</h2>
          <p className="text-indigo-400 font-bold uppercase text-[9px] tracking-[0.4em] italic opacity-80">Sistem de Management Juridic 2.0</p>
        </div>

        <div className="bg-white/5 backdrop-blur-3xl p-10 md:p-12 rounded-[3.5rem] shadow-[0_32px_64px_-16px_rgba(0,0,0,0.6)] border border-white/10 relative overflow-hidden group hover:border-white/20 transition-all duration-500">
          {error && (
            <div className="mb-8 p-4 bg-rose-500/10 border border-rose-500/20 text-rose-400 rounded-2xl text-[10px] font-black uppercase tracking-tight text-center animate-in shake">
              ⚠️ {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <label className="block text-[9px] font-black text-slate-500 uppercase tracking-widest ml-1 italic">Email Profesional</label>
              <input 
                type="email" 
                required 
                value={email} 
                onChange={(e) => setEmail(e.target.value)} 
                className="w-full p-5 bg-[#0f172a] border border-white/5 rounded-2xl outline-none font-bold text-white focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 transition-all placeholder:text-slate-700" 
                placeholder="avocat@lexsmart.ro"
              />
            </div>

            <div className="space-y-2">
              <label className="block text-[9px] font-black text-slate-500 uppercase tracking-widest ml-1 italic">Parolă</label>
              <input 
                type="password" 
                required 
                value={password} 
                onChange={(e) => setPassword(e.target.value)} 
                className="w-full p-5 bg-[#0f172a] border border-white/5 rounded-2xl outline-none font-bold text-white focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 transition-all placeholder:text-slate-700" 
                placeholder="••••••••"
              />
            </div>

            <button 
              type="submit" 
              disabled={loading} 
              className="w-full py-6 bg-indigo-600 text-white rounded-2xl font-black text-[12px] uppercase tracking-[0.3em] shadow-2xl shadow-indigo-600/30 hover:bg-indigo-700 hover:shadow-indigo-600/40 transition-all flex items-center justify-center gap-3 disabled:opacity-50 active:scale-95 mt-4"
            >
              {loading ? "SE PROCESEAZĂ..." : "ACCES CABINET →"}
            </button>
          </form>

          <div className="mt-12 pt-8 border-t border-white/5 flex flex-col gap-4 items-center">
            <button type="button" onClick={handleResetPassword} className="text-[10px] font-black text-rose-400/80 uppercase tracking-widest hover:text-rose-400 transition-colors italic">Ai uitat parola? Resetare securizată</button>
            <button type="button" onClick={() => navigate('/register')} className="text-[10px] font-black text-indigo-400 uppercase tracking-widest hover:text-white transition-colors italic mt-2">Nu ai cont? Creează Unul</button>
            <p className="text-[8px] text-white/10 font-bold uppercase tracking-[0.4em] mt-4">LexSmart Security AES-256</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginView;
